import React, { useState, useEffect, useCallback } from "react";
import {
  searchHistoricalItems,
  getHistoricalFilters,
  getHistoricalStats,
  updateHistoricalItem,
  deleteHistoricalItem,
  bulkDeleteHistoricalItems,
} from "../../services/apiService";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Search,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Info,
  Package,
  Database,
  Loader2,
  ChevronDown,
  Pencil,
  Trash2,
  X,
  Check,
} from "lucide-react";

/**
 * HistoricalSearchDialog - Research the pre-indexed historical costing sheets.
 *
 * Read/curate only: search across 162+ legacy Excel line items, filter by
 * material/pricing, inspect per-department labor, and correct records
 * (edit/delete). The former "import selected as Products" flow is retired —
 * catalog products no longer exist; rate cards + product types are the pricing
 * path. Historical data is the evidence an admin uses to set those rates.
 *
 * Picking a line to price from is NOT this dialog's job: that lives on the
 * Costed Job References screen, which shows quoted hours against Island Time
 * actuals before anyone pulls rates off a sheet.
 */
function HistoricalSearchDialog({ isOpen, onClose }) {
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [materialFilter, setMaterialFilter] = useState("");
  const [pricingFilter, setPricingFilter] = useState("");

  // Results state
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Filter options
  const [materialTypes, setMaterialTypes] = useState([]);

  // Stats
  const [stats, setStats] = useState(null);

  // Selection state
  const [selectedItems, setSelectedItems] = useState(new Set());

  // UI state
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Edit state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load filters, stats, and initial results on mount
  useEffect(() => {
    if (isOpen) {
      loadFiltersAndStats();
      loadAllItems(); // Load all items initially
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadFiltersAndStats = async () => {
    try {
      const [filtersData, statsData] = await Promise.all([
        getHistoricalFilters(),
        getHistoricalStats(),
      ]);
      setMaterialTypes(filtersData.material_types || []);
      setStats(statsData);
    } catch (err) {
      console.error("Failed to load filters/stats:", err);
      // Non-fatal, filters will just be empty
    }
  };

  // Core search function - accepts explicit filter values to avoid stale closures
  const doSearch = async (query, material, pricing) => {
    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      const data = await searchHistoricalItems({
        q: query?.trim() || "", // Empty query returns all items
        material: material || null,
        pricing: pricing || null,
        limit,
        offset: 0,
      });

      setResults(data.results || []);
      setTotalResults(data.total || 0);
      setOffset(0);
      setSelectedItems(new Set());
    } catch (err) {
      console.error("Search failed:", err);
      setError(err.error || err.message || "Search failed");
      setResults([]);
      setTotalResults(0);
    } finally {
      setIsSearching(false);
    }
  };

  const loadAllItems = () => {
    doSearch("", materialFilter, pricingFilter);
  };

  // Search function wrapper for callbacks
  const performSearch = useCallback(
    (query) => {
      doSearch(query, materialFilter, pricingFilter);
    },
    [materialFilter, pricingFilter],
  );

  // Debounced search as user types
  useEffect(() => {
    // Skip if dialog just opened (initial load handles it)
    if (!hasSearched) return;

    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, performSearch, hasSearched]);

  // Re-search when filters change - call doSearch directly with current values
  useEffect(() => {
    // Only trigger if we've already loaded items
    if (hasSearched) {
      doSearch(searchQuery, materialFilter, pricingFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialFilter, pricingFilter]);

  // Manual search handler (for button/enter key)
  const handleSearch = useCallback(() => {
    performSearch(searchQuery);
  }, [searchQuery, performSearch]);

  // Load more results
  const loadMore = async () => {
    const newOffset = offset + limit;
    setIsSearching(true);

    try {
      const data = await searchHistoricalItems({
        q: searchQuery.trim(),
        material: materialFilter || null,
        pricing: pricingFilter || null,
        limit,
        offset: newOffset,
      });

      setResults((prev) => [...prev, ...(data.results || [])]);
      setOffset(newOffset);
    } catch (err) {
      console.error("Load more failed:", err);
      setError(err.error || err.message || "Failed to load more results");
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Select/deselect all
  const selectAll = () => {
    setSelectedItems(new Set(results.map((r) => r.id)));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  // Start editing an item
  const startEditing = (item) => {
    setEditingItemId(item.id);
    setEditForm({
      name: item.name || "",
      description: item.description || "",
      material_type: item.material_type || "",
      pricing_model: item.pricing_model || "",
    });
    setError(null);
    setSuccessMessage(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingItemId(null);
    setEditForm({});
  };

  // Save edited item
  const saveEdit = async () => {
    if (!editingItemId) return;

    setIsSaving(true);
    setError(null);

    try {
      const updatedItem = await updateHistoricalItem(editingItemId, editForm);

      // Update the item in results
      setResults((prev) =>
        prev.map((item) =>
          item.id === editingItemId ? { ...item, ...updatedItem } : item,
        ),
      );

      setSuccessMessage("Item updated successfully");
      setEditingItemId(null);
      setEditForm({});

      // Clear success message after 3s
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.error || err.message || "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Delete a single item
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteHistoricalItem(itemId);

      // Remove from results
      setResults((prev) => prev.filter((item) => item.id !== itemId));
      setTotalResults((prev) => prev - 1);

      // Remove from selection if selected
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });

      setSuccessMessage("Item deleted successfully");
      setTimeout(() => setSuccessMessage(null), 3000);

      // Refresh stats
      loadFiltersAndStats();
    } catch (err) {
      setError(err.error || err.message || "Failed to delete item");
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk delete selected items
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedItems.size} selected item(s)?`,
      )
    )
      return;

    setIsDeleting(true);
    setError(null);

    try {
      const itemIds = Array.from(selectedItems);
      const result = await bulkDeleteHistoricalItems(itemIds);

      // Remove deleted items from results
      setResults((prev) => prev.filter((item) => !selectedItems.has(item.id)));
      setTotalResults((prev) => prev - result.deleted_count);
      setSelectedItems(new Set());

      setSuccessMessage(
        `Deleted ${result.deleted_count} item(s)${result.errors?.length ? ` (${result.errors.length} failed)` : ""}`,
      );
      setTimeout(() => setSuccessMessage(null), 3000);

      // Refresh stats
      loadFiltersAndStats();
    } catch (err) {
      setError(err.error || err.message || "Failed to delete items");
    } finally {
      setIsDeleting(false);
    }
  };

  // Close and reset
  const handleClose = () => {
    setSearchQuery("");
    setMaterialFilter("");
    setPricingFilter("");
    setResults([]);
    setTotalResults(0);
    setOffset(0);
    setSelectedItems(new Set());
    setError(null);
    setSuccessMessage(null);
    setHasSearched(false);
    onClose();
  };

  const formatLaborHours = (hours) => {
    if (hours === null || hours === undefined) return "—";
    return `${Number(hours).toFixed(2)} hrs`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return `$${Number(amount).toFixed(2)}`;
  };

  /**
   * Get pricing model badge styling
   */
  const getPricingModelBadge = (pricingModel, sqftRate) => {
    if (!pricingModel) return null;

    const isSqft = pricingModel === "sqft";
    // Use CSS variables with opacity for theme-compatible badges
    const badgeStyle = isSqft
      ? {
          backgroundColor: "var(--accent-purple-bg, rgba(147, 51, 234, 0.15))",
          color: "var(--accent-purple-text, #7c3aed)",
          borderColor: "var(--accent-purple-border, rgba(147, 51, 234, 0.3))",
        }
      : {
          backgroundColor: "var(--accent-blue-bg, rgba(59, 130, 246, 0.15))",
          color: "var(--accent-blue-text, #3b82f6)",
          borderColor: "var(--accent-blue-border, rgba(59, 130, 246, 0.3))",
        };

    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border"
        style={badgeStyle}
        title={
          isSqft && sqftRate
            ? `Square foot pricing: $${Number(sqftRate).toFixed(2)}/sqft`
            : "Formula-based pricing (Material costs + Labor hours)"
        }
      >
        {isSqft ? "$/sqft" : "Mat+Lab"}
        {isSqft && sqftRate && (
          <span className="ml-1 font-bold">${Number(sqftRate).toFixed(2)}</span>
        )}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[var(--wb-accent-solid)]" />
            Search Historical Costing Sheets
          </DialogTitle>
          <DialogDescription>
            Search across {stats?.total_items || "---"} line items from{" "}
            {stats?.unique_files || "---"} historical estimates — the evidence
            behind your rate cards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pb-20">
          {/* Search Controls */}
          {/* Search Input */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
              style={{ color: "var(--wb-dim)" }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by name or job number (e.g., Bracket, IM6202)..."
              className="w-full pl-10 pr-10 py-2 rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--input-bg, var(--wb-pane))",
                borderColor: "var(--wb-line)",
                borderWidth: "1px",
                borderStyle: "solid",
                color: "var(--wb-ink)",
              }}
              autoFocus
            />
            {isSearching && (
              <Loader2
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin"
                style={{ color: "var(--primary-color, #3b82f6)" }}
              />
            )}
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "var(--wb-ink)" }}
              >
                Material Type
              </label>
              <div className="relative">
                <select
                  value={materialFilter}
                  onChange={(e) => setMaterialFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg appearance-none focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: "var(--input-bg, var(--wb-pane))",
                    borderColor: "var(--wb-line)",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    color: "var(--wb-ink)",
                  }}
                >
                  <option value="">All Materials</option>
                  {materialTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none"
                  style={{ color: "var(--wb-dim)" }}
                />
              </div>
            </div>
            <div className="flex-1">
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "var(--wb-ink)" }}
              >
                Pricing Model
              </label>
              <div className="relative">
                <select
                  value={pricingFilter}
                  onChange={(e) => setPricingFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg appearance-none focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: "var(--input-bg, var(--wb-pane))",
                    borderColor: "var(--wb-line)",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    color: "var(--wb-ink)",
                  }}
                >
                  <option value="">All Pricing Models</option>
                  <option value="formula">Formula (Mat+Lab)</option>
                  <option value="sqft">Square Foot ($/sqft)</option>
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none"
                  style={{ color: "var(--wb-dim)" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <Alert
            style={{
              backgroundColor: "var(--success-bg, rgba(34, 197, 94, 0.15))",
              borderColor: "var(--success-border, rgba(34, 197, 94, 0.3))",
            }}
          >
            <CheckCircle2
              className="h-4 w-4"
              style={{ color: "var(--success-text, #22c55e)" }}
            />
            <AlertDescription style={{ color: "var(--success-text, #22c55e)" }}>
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Results Area - Fixed minimum height to prevent layout shifts */}
        <div className="min-h-[350px] transition-all duration-200">
          {/* Initial state message */}
          {!hasSearched && !isSearching && (
            <div
              className="flex flex-col items-center justify-center h-[350px]"
              style={{ color: "var(--wb-dim)" }}
            >
              <Database
                className="w-16 h-16 mb-4"
                style={{ color: "var(--wb-dim)", opacity: 0.5 }}
              />
              <p
                className="text-lg font-medium"
                style={{ color: "var(--wb-ink)" }}
              >
                Search Historical Estimates
              </p>
              <p className="text-sm mt-1">
                Start typing above — results appear as you type
              </p>
              {stats && (
                <p className="text-xs mt-4" style={{ opacity: 0.7 }}>
                  {stats.total_items} items indexed from {stats.unique_files}{" "}
                  files
                </p>
              )}
            </div>
          )}

          {/* Loading state */}
          {isSearching && !hasSearched && (
            <div
              className="flex flex-col items-center justify-center h-[350px]"
              style={{ color: "var(--wb-dim)" }}
            >
              <Loader2
                className="w-12 h-12 mb-4 animate-spin"
                style={{ color: "var(--primary-color, #3b82f6)" }}
              />
              <p className="text-sm">Searching...</p>
            </div>
          )}

          {/* Results */}
          {hasSearched && (
            <div className="space-y-4">
              {/* Results Header */}
              <div
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: "var(--hover-bg, rgba(0,0,0,0.05))" }}
              >
                <div className="flex items-center gap-2">
                  <Package
                    className="w-4 h-4"
                    style={{ color: "var(--wb-dim)" }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "var(--wb-ink)" }}
                  >
                    {totalResults} result{totalResults !== 1 ? "s" : ""} found
                  </span>
                  {isSearching && (
                    <Loader2
                      className="w-3 h-3 animate-spin"
                      style={{ color: "var(--primary-color, #3b82f6)" }}
                    />
                  )}
                </div>
                {results.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAll}>
                      Deselect All
                    </Button>
                    {selectedItems.size > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={isDeleting}
                        style={{
                          color: "var(--danger-text, #dc2626)",
                          borderColor:
                            "var(--danger-border, rgba(220, 38, 38, 0.3))",
                        }}
                      >
                        {isDeleting ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3 mr-1" />
                        )}
                        Delete Selected
                      </Button>
                    )}
                    <span
                      className="text-sm ml-2"
                      style={{ color: "var(--wb-dim)" }}
                    >
                      {selectedItems.size} selected
                    </span>
                  </div>
                )}
              </div>

              {/* Results Table */}
              {results.length > 0 ? (
                <div
                  className="rounded-lg overflow-hidden"
                  style={{
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: "var(--wb-line)",
                  }}
                >
                  <table
                    className="w-full text-sm"
                    style={{ color: "var(--wb-ink)" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "var(--hover-bg, rgba(0,0,0,0.05))",
                        borderBottom: "1px solid var(--wb-line)",
                      }}
                    >
                      <tr>
                        <th className="w-10 px-3 py-2"></th>
                        <th className="px-3 py-2 text-left font-medium">
                          Name
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Source
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Material
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Pricing
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Unit Cost
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Labor/Unit
                        </th>
                        <th className="w-20 px-3 py-2 text-center font-medium">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((item) => {
                        const isSelected = selectedItems.has(item.id);
                        const isEditing = editingItemId === item.id;

                        if (isEditing) {
                          // Editing row
                          const editInputStyle = {
                            backgroundColor: "var(--input-bg, var(--wb-pane))",
                            borderColor: "var(--wb-line)",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            color: "var(--wb-ink)",
                          };
                          return (
                            <tr
                              key={item.id}
                              style={{
                                backgroundColor:
                                  "var(--warning-bg, rgba(234, 179, 8, 0.15))",
                                borderBottom: "1px solid var(--wb-line)",
                              }}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled
                                  className="rounded opacity-50"
                                  style={{ borderColor: "var(--wb-line)" }}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      name: e.target.value,
                                    }))
                                  }
                                  className="w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1"
                                  style={editInputStyle}
                                  placeholder="Name"
                                />
                                <input
                                  type="text"
                                  value={editForm.description}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      description: e.target.value,
                                    }))
                                  }
                                  className="w-full px-2 py-1 mt-1 text-xs rounded focus:outline-none focus:ring-1"
                                  style={editInputStyle}
                                  placeholder="Description"
                                />
                              </td>
                              <td
                                className="px-3 py-2 text-xs"
                                style={{ color: "var(--wb-dim)" }}
                              >
                                {item.source_file}
                                {item.job_number && (
                                  <span className="block">
                                    {item.job_number}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={editForm.material_type}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      material_type: e.target.value,
                                    }))
                                  }
                                  className="w-full px-2 py-1 text-xs rounded focus:outline-none focus:ring-1"
                                  style={editInputStyle}
                                >
                                  <option value="">-- Select --</option>
                                  {materialTypes.map((mt) => (
                                    <option key={mt} value={mt}>
                                      {mt}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={editForm.pricing_model}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      pricing_model: e.target.value,
                                    }))
                                  }
                                  className="w-full px-2 py-1 text-xs rounded focus:outline-none focus:ring-1"
                                  style={editInputStyle}
                                >
                                  <option value="">-- Select --</option>
                                  <option value="formula">Formula</option>
                                  <option value="sqft">Sq Ft</option>
                                </select>
                              </td>
                              <td
                                className="px-3 py-2 text-sm"
                                style={{ color: "var(--wb-dim)" }}
                              >
                                {formatCurrency(item.unit_cost)}
                              </td>
                              <td
                                className="px-3 py-2 text-sm"
                                style={{ color: "var(--wb-dim)" }}
                              >
                                {formatLaborHours(item.labor_per_unit)}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={saveEdit}
                                    disabled={isSaving}
                                    className="wb-plain p-1.5 rounded hover:opacity-70 disabled:opacity-50"
                                    style={{
                                      color: "var(--success-text, #22c55e)",
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                      border: "none",
                                      margin: 0,
                                    }}
                                    title="Save changes"
                                  >
                                    {isSaving ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Check className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    disabled={isSaving}
                                    className="wb-plain p-1.5 rounded hover:opacity-70 disabled:opacity-50"
                                    style={{
                                      color: "var(--wb-dim)",
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                      border: "none",
                                      margin: 0,
                                    }}
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        // Normal display row
                        return (
                          <tr
                            key={item.id}
                            className="cursor-pointer"
                            style={{
                              backgroundColor: isSelected
                                ? "var(--selected-bg, rgba(59, 130, 246, 0.15))"
                                : "transparent",
                              borderBottom: "1px solid var(--wb-line)",
                            }}
                            onClick={() => toggleItemSelection(item.id)}
                            onMouseEnter={(e) => {
                              if (!isSelected)
                                e.currentTarget.style.backgroundColor =
                                  "var(--hover-bg, rgba(0,0,0,0.05))";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected)
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                            }}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(item.id)}
                                className="rounded"
                                style={{ borderColor: "var(--wb-line)" }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{item.name}</div>
                              {item.description && (
                                <p
                                  className="text-xs mt-1 truncate max-w-[250px]"
                                  style={{ color: "var(--wb-dim)" }}
                                  title={item.description}
                                >
                                  {item.description}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <FileSpreadsheet
                                  className="w-3 h-3"
                                  style={{ color: "var(--wb-dim)" }}
                                />
                                <span className="text-xs">
                                  {item.source_file}
                                </span>
                              </div>
                              {item.job_number && (
                                <span
                                  className="text-xs"
                                  style={{ color: "var(--wb-dim)" }}
                                >
                                  {item.job_number}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {item.material_type ? (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                  style={{
                                    backgroundColor:
                                      "var(--hover-bg, rgba(0,0,0,0.08))",
                                    color: "var(--wb-ink)",
                                  }}
                                >
                                  {item.material_type}
                                </span>
                              ) : (
                                <span style={{ color: "var(--wb-dim)" }}>
                                  Unknown
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {getPricingModelBadge(
                                item.pricing_model,
                                item.sqft_rate,
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-medium">
                                {formatCurrency(item.unit_cost)}
                              </span>
                              {item.quantity && (
                                <span
                                  className="block text-xs"
                                  style={{ color: "var(--wb-dim)" }}
                                >
                                  Qty: {item.quantity}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className="cursor-help"
                                title={`CNC: ${formatLaborHours(item.cnc_hours)}
Cut: ${formatLaborHours(item.cut_hours)}
Fab: ${formatLaborHours(item.fab_hours)}
Paint: ${formatLaborHours(item.paint_hours)}`}
                              >
                                {formatLaborHours(item.labor_per_unit)}
                                <Info
                                  className="w-3 h-3 inline ml-1"
                                  style={{ color: "var(--wb-dim)" }}
                                />
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <div
                                className="flex items-center justify-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={() => startEditing(item)}
                                  className="wb-plain p-1.5 rounded hover:opacity-70"
                                  style={{
                                    color: "var(--primary-color, #3b82f6)",
                                    backgroundColor: "transparent",
                                    boxShadow: "none",
                                    border: "none",
                                    margin: 0,
                                  }}
                                  title="Edit item"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  disabled={isDeleting}
                                  className="wb-plain p-1.5 rounded hover:opacity-70 disabled:opacity-50"
                                  style={{
                                    color: "var(--danger-text, #dc2626)",
                                    backgroundColor: "transparent",
                                    boxShadow: "none",
                                    border: "none",
                                    margin: 0,
                                  }}
                                  title="Delete item"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                !isSearching && (
                  <div
                    className="text-center py-8"
                    style={{ color: "var(--wb-dim)" }}
                  >
                    <Search
                      className="w-12 h-12 mx-auto mb-4"
                      style={{ color: "var(--wb-dim)", opacity: 0.5 }}
                    />
                    <p>No results found for &quot;{searchQuery}&quot;</p>
                    <p className="text-sm mt-1">
                      Try a different search term or adjust filters
                    </p>
                  </div>
                )
              )}

              {/* Load More */}
              {results.length < totalResults && (
                <div className="text-center">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={isSearching}
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>Load More ({totalResults - results.length} remaining)</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter
          className="sticky bottom-0 z-20 pt-3"
          style={{
            borderTop: "1px solid var(--wb-line)",
            backgroundColor: "var(--wb-pane)",
          }}
        >
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default HistoricalSearchDialog;
